'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { useTranslations, useLocale } from 'next-intl'
import LangToggle from './LangToggle'
import ContactButton from './ContactButton'
import type { Locale } from './useLocale'

interface Props {
  open:      boolean
  onClose:   () => void
  locale:    Locale
  setLocale: (next: Locale) => void
  onContact: () => void
}

const LINKS = [
  { key: 'home',  hrefEs: '/',           hrefEn: '/'      },
  { key: 'tech',  hrefEs: '/tecnologia', hrefEn: '/tech'  },
  { key: 'about', hrefEs: '/about',      hrefEn: '/about' },
] as const

const CREAM    = '#F5ECD8'
const CREAM_40 = 'rgba(245,238,217,0.40)'

export default function MobileMenu({ open, onClose, locale, setLocale, onContact }: Props) {
  const t = useTranslations('nav')
  const rawLocale = useLocale()
  const isEs = rawLocale !== 'en'

  // Lock body scroll while open. Use position:fixed + top offset so iOS
  // Safari (which ignores html/body overflow:hidden in many cases) is
  // also pinned. Restore exact scroll position on close.
  useEffect(() => {
    if (!open) return
    const scrollY = window.scrollY
    const body  = document.body
    const html  = document.documentElement
    const prevBodyPosition  = body.style.position
    const prevBodyTop       = body.style.top
    const prevBodyWidth     = body.style.width
    const prevBodyOverflow  = body.style.overflow
    const prevHtmlOverflow  = html.style.overflow
    body.style.position = 'fixed'
    body.style.top      = `-${scrollY}px`
    body.style.width    = '100%'
    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'
    return () => {
      body.style.position = prevBodyPosition
      body.style.top      = prevBodyTop
      body.style.width    = prevBodyWidth
      body.style.overflow = prevBodyOverflow
      html.style.overflow = prevHtmlOverflow
      window.scrollTo(0, scrollY)
    }
  }, [open])

  // Escape closes
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      <style>{`
        .mm-overlay {
          position: fixed;
          inset: 0;
          background: rgba(31,22,8,0.96);
          backdrop-filter: blur(20px) saturate(160%);
          -webkit-backdrop-filter: blur(20px) saturate(160%);
          z-index: 90;
          display: flex;
          flex-direction: column;
          padding: 96px 28px 36px;
          transform: translateX(100%);
          transition: transform 380ms cubic-bezier(0.22, 1, 0.36, 1),
                      visibility 0s linear 380ms;
          visibility: hidden;
          pointer-events: none;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .mm-overlay[data-open="true"] {
          transform: translateX(0);
          visibility: visible;
          transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1),
                      visibility 0s linear 0s;
          pointer-events: auto;
        }
        .mm-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
          justify-content: center;
        }
        .mm-link {
          display: block;
          padding: 14px 0;
          font-family: var(--font-display);
          font-weight: 500;
          font-size: clamp(34px, 9vw, 52px);
          line-height: 1.05;
          letter-spacing: -0.015em;
          color: ${CREAM};
          text-decoration: none;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 320ms ease, transform 320ms cubic-bezier(0.22,1,0.36,1), color 220ms ease;
        }
        .mm-link:active { color: var(--lime-500, #9FAF08); }
        .mm-overlay[data-open="true"] .mm-link {
          opacity: 1;
          transform: translateY(0);
        }
        /* Stagger the entrance */
        .mm-overlay[data-open="true"] li:nth-child(1) .mm-link { transition-delay: 80ms; }
        .mm-overlay[data-open="true"] li:nth-child(2) .mm-link { transition-delay: 140ms; }
        .mm-overlay[data-open="true"] li:nth-child(3) .mm-link { transition-delay: 200ms; }
        .mm-overlay[data-open="true"] li:nth-child(4) { transition-delay: 280ms; }

        .mm-cta-row {
          margin-top: 28px;
          padding-top: 24px;
          border-top: 1px solid rgba(245,236,217,0.12);
        }
        .mm-cta-row button {
          width: 100% !important;
          height: 56px !important;
          padding: 0 24px !important;
          font-size: 14px !important;
          letter-spacing: 0.06em !important;
          justify-content: center !important;
          gap: 12px !important;
          border-radius: 14px !important;
        }
        .mm-bottom {
          margin-top: 32px;
          display: flex; justify-content: center;
          opacity: 0;
          transition: opacity 320ms ease 320ms;
        }
        .mm-overlay[data-open="true"] .mm-bottom { opacity: 1; }
        .mm-footer {
          margin-top: 24px;
          font-family: var(--font-body);
          font-size: 10px;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: ${CREAM_40};
          text-align: center;
          opacity: 0;
          transition: opacity 320ms ease 380ms;
        }
        .mm-overlay[data-open="true"] .mm-footer { opacity: 1; }

        @media (prefers-reduced-motion: reduce) {
          .mm-overlay { transition: none !important; }
          .mm-link, .mm-bottom, .mm-footer { transition: none !important; }
        }
      `}</style>

      <div
        id="mobile-menu"
        className="mm-overlay"
        data-open={open || undefined}
        aria-hidden={!open}
        role="dialog"
        aria-modal="true"
        aria-label={t('menuOpen')}
      >
        <ul className="mm-list">
          {LINKS.map(({ key, hrefEs, hrefEn }) => (
            <li key={key}>
              <Link
                href={isEs ? hrefEs : hrefEn}
                onClick={onClose}
                className="mm-link"
              >
                {t(key)}
              </Link>
            </li>
          ))}
          <li className="mm-cta-row">
            <ContactButton
              onClick={() => {
                onClose()
                setTimeout(() => onContact(), 280)
              }}
            />
          </li>
        </ul>

        <div className="mm-bottom">
          <LangToggle
            locale={locale}
            onChange={setLocale}
            isDark
            size="lg"
          />
        </div>

        <div className="mm-footer">
          {t('mobileFooter')}
        </div>
      </div>
    </>
  )
}
